import type { PosPromotionPreviewResponse } from '@eshop/types/offers.types.ts';

import type { PosSaleLine } from '../types';
import { clampCurrency, computeLineAmounts, type OrderSummary } from '../components/discountMath';

export interface PromotionLineAdjustment {
  amount: number;
  label: string;
}

export interface EffectiveOrderSummary extends OrderSummary {
  promotionLineAdjustments: Record<number, PromotionLineAdjustment>;
  promotionApplied: boolean;
}

const toDiscountMap = (
  entries: Array<{ item_id: number; discount: number }> | undefined,
): Record<number, number> => (entries ?? []).reduce<Record<number, number>>((result, entry) => {
    const itemId = Number(entry.item_id);
    const discount = clampCurrency(Number(entry.discount));
    if (Number.isInteger(itemId) && discount > 0) {
      result[itemId] = clampCurrency((result[itemId] ?? 0) + discount);
    }
    return result;
  }, {});

const getPromotionLabel = (
  entry: { name?: string | null; coupon_code?: string | null } | undefined,
  fallback?: { name?: string | null; coupon_code?: string | null },
): string => {
  const couponCode = entry?.coupon_code || fallback?.coupon_code;
  if (couponCode) {
    return `Cupón ${couponCode}`;
  }
  return entry?.name?.trim() || fallback?.name?.trim() || 'Promoción';
};

const normalizeLineDiscounts = (
  preview: PosPromotionPreviewResponse | undefined,
  lines: PosSaleLine[],
): Record<number, number> => {
  if (!preview || (preview.source !== 'promotion' && preview.source !== 'mixed')) {
    return {};
  }
  const finalDiscounts = toDiscountMap(preview.line_discounts);
  const manualDiscounts = toDiscountMap(preview.manual_line_discounts);
  return lines.reduce<Record<number, number>>((result, line) => {
    const itemId = line.item.id;
    const effective = preview.line_discounts?.find((entry) => Number(entry.item_id) === itemId);
    const isPromotionWinner =
      effective?.source === 'promotion' ||
      (effective?.source == null && (manualDiscounts[itemId] ?? 0) <= 0);
    if (isPromotionWinner && finalDiscounts[itemId] > 0) {
      result[itemId] = finalDiscounts[itemId];
    }
    return result;
  }, {});
};

export const deriveEffectiveOrderSummary = (
  base: OrderSummary,
  lines: PosSaleLine[],
  preview?: PosPromotionPreviewResponse,
): EffectiveOrderSummary => {
  const lineDiscounts = normalizeLineDiscounts(preview, lines);
  const usesAuthoritativeManualGlobal = Boolean(preview?.manual_global_won);
  const promotionApplied =
    (preview?.source === 'promotion' || preview?.source === 'mixed') && preview.discount > 0;
  if (!preview || (!promotionApplied && !usesAuthoritativeManualGlobal)) {
    return {
      ...base,
      promotionLineAdjustments: {},
      promotionApplied: false,
    };
  }

  let taxes = 0;
  const promotionLineAdjustments: Record<number, PromotionLineAdjustment> = {};
  const manualDiscounts = toDiscountMap(preview?.manual_line_discounts);
  for (const line of lines) {
    const gross = computeLineAmounts({ ...line, manualDiscount: undefined }).baseSubtotal;
    const effectiveLine = preview?.line_discounts?.find(
      (entry) => Number(entry.item_id) === line.item.id,
    );
    const discount = clampCurrency(
      effectiveLine ? Number(effectiveLine.discount) : lineDiscounts[line.item.id] ?? 0,
    );
    if (discount > 0) {
      const isPromotionWinner =
        effectiveLine?.source === 'promotion' ||
        (effectiveLine?.source == null && (manualDiscounts[line.item.id] ?? 0) <= 0);
      if (isPromotionWinner) {
        const appliedPromotion = preview.applied.find(
          (entry) => Number(entry.promotion_id) === Number(effectiveLine?.promotion_id),
        ) ?? preview.applied[0];
        promotionLineAdjustments[line.item.id] = {
          amount: discount,
          label: getPromotionLabel(effectiveLine, appliedPromotion),
        };
      }
    }
    const net = clampCurrency(Math.max(gross - discount, 0));
    const taxRate = Number(line.item.price?.tax_rate ?? 0);
    taxes += clampCurrency(net * (taxRate > 1 ? taxRate / 100 : taxRate));
  }

  const subtotal = clampCurrency(Number(preview.total));
  const taxesRounded = clampCurrency(taxes);
  const total = clampCurrency(subtotal + taxesRounded + base.shipping);
  return {
    ...base,
    subtotal,
    taxes: taxesRounded,
    discounts: clampCurrency(Number(preview.discount)),
    total,
    promotionLineAdjustments,
    promotionApplied,
  };
};

export default deriveEffectiveOrderSummary;
